// src/pages/admin/AdminDashboard.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { useAuth } from "../../hooks/useAuth";
import { useTranslation } from "../../hooks/useTranslation";
import { C, FONT } from "../../theme";

// ── Role type ─────────────────────────────────────────────────────────────────
type UserRole = "admin" | "coach" | "ranking_admin";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

const AdminDashboard = () => {
  const { currentUser, userRole } = useAuth();
  const { t } = useTranslation();
  const w = useWindowWidth();
  const isMobile = w < 768;

  // Cast for typed comparisons
  const role = userRole as UserRole | null;

  // ── All possible section cards ────────────────────────────────────────────
  const allSections = [
    {
      key: "athletes",
      label: t("admin.dashboard.athletes.title"),
      emoji: "🏃",
      path: "/admin/athletes",
      desc: t("admin.dashboard.athletes.desc"),
      ready: true,
    },
    {
      key: "clubs",
      label: t("admin.dashboard.clubs.title"),
      emoji: "🏫",
      path: "/admin/clubs",
      desc: t("admin.dashboard.clubs.desc"),
      ready: true,
    },
    {
      key: "competitions",
      label: t("admin.dashboard.competitions.title"),
      emoji: "🏆",
      path: "/admin/competitions",
      desc: t("admin.dashboard.competitions.desc"),
      ready: true,
    },
    {
      key: "registrations",
      label: t("admin.dashboard.registrations.title"),
      emoji: "📋",
      path: "/admin/registrations",
      desc: t("admin.dashboard.registrations.desc"),
      ready: true,
    },
    {
      key: "recentRegistrations",
      label: t("admin.dashboard.recentRegistrations.title"),
      emoji: "🕒",
      path: "/admin/recent-registrations",
      desc: t("admin.dashboard.recentRegistrations.desc"),
      ready: true,
    },
    {
      key: "rankings",
      label: t("admin.dashboard.rankings.title"),
      emoji: "🏅",
      path: "/admin/rankings",
      desc: t("admin.dashboard.rankings.desc"),
      ready: true,
    },
    {
      key: "draws",
      label: t("admin.dashboard.draws.title"),
      emoji: "\u2694\uFE0F",
      path: "/admin/draw-lab",
      desc: t("admin.dashboard.draws.desc"),
      ready: true,
    },
    {
      key: "affiliations",
      label: t("admin.dashboard.affiliations.title"),
      emoji: "🏛️",
      path: "/admin/affiliations",
      desc: t("admin.dashboard.affiliations.desc"),
      ready: true,
    },
    {
      key: "credits",
      label: t("credits.heading"),
      emoji: "🎟️",
      path: "/admin/credits",
      desc: t("admin.dashboard.credits.desc"),
      ready: true,
    },
  ];

  // ── Keys visible to ranking_admin (financial cards excluded) ───────────────
  const rankingAdminAllowedKeys = ["athletes", "clubs", "competitions", "rankings", "draws"];

  // Filter sections based on role
  const sections =
    role === "ranking_admin"
      ? allSections.filter((sec) => rankingAdminAllowedKeys.includes(sec.key))
      : allSections;

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={s.container}>
        <div style={s.header}>
          <div style={s.pageLabel}>{t("admin.dashboard.pageLabel")}</div>
          <div style={{ ...s.heading, fontSize: isMobile ? "28px" : "42px" }}>{t("admin.dashboard.heading")}</div>
          <div style={s.subheading}>
            {t("admin.dashboard.welcome")}{" "}
            <span style={{ color: C.text }}>{currentUser?.email}</span>
          </div>
        </div>

        <div style={{ ...s.statsStrip, flexWrap: "wrap" as const }}>
          <div style={s.statItem}>
            <div style={s.statValue}>GEO</div>
            <div style={s.statLabel}>{t("admin.dashboard.stats.federation")}</div>
          </div>
          <div style={s.statDivider} />
          <div style={s.statItem}>
            <div style={s.statValue}>TKD</div>
            <div style={s.statLabel}>{t("admin.dashboard.stats.sport")}</div>
          </div>
          <div style={s.statDivider} />
          <div style={s.statItem}>
            <div style={s.statValue}>v1.0</div>
            <div style={s.statLabel}>{t("admin.dashboard.stats.system")}</div>
          </div>
        </div>

        <div style={s.sectionLabel}>{t("admin.dashboard.modules")}</div>

        <div style={{ ...s.grid, gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fill, minmax(240px, 1fr))" }}>
          {sections.map((sec) => (
            <Link
              to={sec.ready ? sec.path : "#"}
              key={sec.path}
              style={s.cardLink}
            >
              <div style={{ ...s.card, ...(sec.ready ? {} : s.cardDisabled) }}>
                <div style={s.cardAccent} />
                <div style={s.cardBody}>
                  <div style={s.cardEmoji}>{sec.emoji}</div>
                  <div style={s.cardLabel}>{sec.label}</div>
                  <div style={s.cardDesc}>{sec.desc}</div>
                  {!sec.ready && (
                    <div style={s.comingSoon}>{t("admin.dashboard.comingSoon")}</div>
                  )}
                  {sec.ready && <div style={s.cardArrow}>→</div>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1200px", margin: "0 auto", padding: "40px 24px" },
  header: { marginBottom: "32px" },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "6px",
  },
  heading: {
    fontFamily: FONT.display,
    fontSize: "42px",
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
    marginBottom: "8px",
  },
  subheading: { fontSize: "14px", color: C.text2 },
  statsStrip: {
    display: "flex",
    alignItems: "center",
    backgroundColor: C.surface,
    borderRadius: "10px",
    border: `1px solid ${C.border}`,
    marginBottom: "36px",
    overflow: "hidden",
  },
  statItem: { flex: 1, padding: "16px 24px", textAlign: "center" },
  statValue: {
    fontFamily: FONT.display,
    fontSize: "20px",
    letterSpacing: "2px",
    color: C.text,
  },
  statLabel: {
    fontSize: "10px",
    color: C.text3,
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginTop: "2px",
  },
  statDivider: { width: "1px", height: "40px", backgroundColor: C.border },
  sectionLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.text2,
    textTransform: "uppercase",
    marginBottom: "16px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: "16px",
  },
  cardLink: { textDecoration: "none" },
  card: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    overflow: "hidden",
    transition: "transform 0.2s, box-shadow 0.2s",
    cursor: "pointer",
    height: "100%",
  },
  cardDisabled: { opacity: 0.45, cursor: "default" },
  cardAccent: { height: "3px", background: C.red },
  cardBody: { padding: "22px 20px" },
  cardEmoji: { fontSize: "28px", marginBottom: "12px" },
  cardLabel: {
    fontFamily: FONT.display,
    fontSize: "22px",
    letterSpacing: "1px",
    color: C.text,
    marginBottom: "8px",
    lineHeight: 1,
  },
  cardDesc: { fontSize: "12px", color: C.text2, lineHeight: 1.6, marginBottom: "16px" },
  cardArrow: { fontSize: "18px", color: C.red, fontWeight: 700 },
  comingSoon: {
    display: "inline-block",
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "20px",
    padding: "3px 10px",
    fontSize: "10px",
    fontWeight: 600,
    color: C.text3,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
};

export default AdminDashboard;
